import {
  WAREHOUSE_KIND_LABEL,
  adjustmentInputSchema,
  warehouseInputSchema,
  warehouseUpdateSchema,
  type Adjustment,
  type Page,
  type Warehouse,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { apiFetch } from "@/lib/api";
import { issuesToErrors, useForm } from "../cadastros/lib";
import { Pager, SaveError, StatusBadge } from "../cadastros/widgets";
import { useAutoCode } from "../cadastros/auto-code";
import { CodeField } from "../cadastros/CodeField";
import { dateTimeBR, qty, unitsOf, useDocAction, useProductDetail, useWarehouses } from "./lib";
import {
  DocStatusBadge,
  LotSelect,
  ProductPicker,
  ReverseAction,
  WarehouseSelect,
} from "./pickers";

// ------------------------------------------------------------------ ajustes

type Line = {
  key: number;
  productId: string;
  productLabel: string;
  unitId: string;
  direction: "entrada" | "saida";
  quantity: string;
  lotMode: "existente" | "novo";
  lotId: string;
  newLotCode: string;
  newLotExpires: string;
};
const emptyLine = (key: number): Line => ({
  key,
  productId: "",
  productLabel: "",
  unitId: "",
  direction: "entrada",
  quantity: "",
  lotMode: "existente",
  lotId: "",
  newLotCode: "",
  newLotExpires: "",
});

export function AjustesPage() {
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const list = useQuery({
    queryKey: ["estoque", "ajustes", offset],
    queryFn: () => apiFetch<Page<Adjustment>>(`/estoque/ajustes?limit=50&offset=${offset}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Ajustes de estoque"
        description="Entrada ou saída com motivo (quebra, amostra, estoque inicial...). Lançado e confirmado na hora; correção é por estorno."
        actions={
          can("estoque.ajuste.lancar") && (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              Novo ajuste
            </Button>
          )
        }
      />
      {creating && (
        <AdjustmentForm onClose={() => setCreating(false)} onSaved={(id) => setViewing(id)} />
      )}
      {viewing && <AdjustmentView id={viewing} onClose={() => setViewing(null)} />}
      <Card>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Número", "Quando", "Depósito", "Motivo", "Itens", "Situação", ""]}>
              {list.data?.items.map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-2 font-mono">{a.number}</td>
                  <td className="px-3 py-2">{dateTimeBR(a.confirmedAt ?? a.createdAt)}</td>
                  <td className="px-3 py-2">{a.warehouseName}</td>
                  <td className="px-3 py-2">{a.reason}</td>
                  <td className="px-3 py-2">{a.itemCount}</td>
                  <td className="px-3 py-2">
                    <DocStatusBadge status={a.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setViewing(a.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function AdjustmentForm({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const whs = useWarehouses();
  const defaultWh = whs.data?.find((w) => w.isDefault)?.id ?? "";
  const [warehouseId, setWarehouseId] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<Line[]>([emptyLine(1)]);
  const [formError, setFormError] = useState<string | null>(null);
  const wh = warehouseId || defaultWh;
  const save = useDocAction<unknown, Adjustment>({
    path: () => "/estoque/ajustes",
    invalidate: [["estoque"]],
    onDone: (r) => {
      onClose();
      onSaved(r.id);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = {
      warehouseId: wh,
      reason,
      items: lines.map((l) => ({
        productId: l.productId,
        unitId: l.unitId,
        direction: l.direction,
        quantity: l.quantity,
        lotId: l.lotMode === "existente" ? l.lotId : null,
        newLot:
          l.lotMode === "novo" && l.newLotCode
            ? { code: l.newLotCode, expiresOn: l.newLotExpires }
            : null,
      })),
    };
    const parsed = adjustmentInputSchema.safeParse(body);
    if (!parsed.success) {
      const i = parsed.error.issues[0];
      return setFormError(
        `${i?.path.length ? `Linha/campo ${i.path.join(".")}: ` : ""}${i?.message ?? "Dados inválidos"}`,
      );
    }
    setFormError(null);
    save.mutate(parsed.data);
  }

  return (
    <Card title="Novo ajuste">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Depósito">
            <WarehouseSelect value={wh} onChange={setWarehouseId} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Motivo" hint="Obrigatório: aparece na auditoria e no livro">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex.: estoque inicial, quebra no transporte"
              />
            </Field>
          </div>
        </div>
        {lines.map((l, i) => (
          <AdjustmentLine
            key={l.key}
            line={l}
            warehouseId={wh}
            onChange={(v) => setLines((ls) => ls.map((x) => (x.key === v.key ? v : x)))}
            onRemove={
              lines.length > 1 ? () => setLines(lines.filter((_, j) => j !== i)) : undefined
            }
          />
        ))}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setLines([...lines, emptyLine(Math.max(...lines.map((x) => x.key)) + 1)])}
        >
          Adicionar item
        </Button>
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            Confirmar ajuste
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={save.error} />
      </form>
    </Card>
  );
}

function AdjustmentLine({
  line,
  warehouseId,
  onChange,
  onRemove,
}: {
  line: Line;
  warehouseId: string;
  onChange: (l: Line) => void;
  onRemove: (() => void) | undefined;
}) {
  const product = useProductDetail(line.productId);
  const units = unitsOf(product.data);
  const firstUnit = units[0]?.id;
  // escolhido o produto, assume a unidade-base até o usuário trocar
  useEffect(() => {
    if (!line.unitId && firstUnit) onChange({ ...line, unitId: firstUnit });
  }, [line, firstUnit, onChange]);
  const set = <K extends keyof Line>(k: K, v: Line[K]) => onChange({ ...line, [k]: v });
  return (
    <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-12 sm:items-end">
      <div className="sm:col-span-4">
        <Field label="Produto">
          <ProductPicker
            value={line.productId}
            label={line.productLabel}
            onChange={(id, label) =>
              onChange({ ...line, productId: id, productLabel: label, unitId: "", lotId: "" })
            }
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Movimento">
          <Select
            value={line.direction}
            onChange={(e) =>
              onChange({
                ...line,
                direction: e.target.value as Line["direction"],
                lotMode: "existente",
              })
            }
          >
            <option value="entrada">Entrada (+)</option>
            <option value="saida">Saída (−)</option>
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Unidade">
          <Select
            value={line.unitId || units[0]?.id || ""}
            onChange={(e) => set("unitId", e.target.value)}
            disabled={!product.data}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
                {u.factor !== "1" ? ` (${qty(u.factor)} ${product.data?.baseUnitCode})` : ""}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Quantidade">
          <Input
            inputMode="decimal"
            value={line.quantity}
            onChange={(e) =>
              onChange({
                ...line,
                quantity: e.target.value,
                unitId: line.unitId || units[0]?.id || "",
              })
            }
          />
        </Field>
      </div>
      <div className="sm:col-span-2 text-right">
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove}>
            Remover
          </Button>
        )}
      </div>
      {product.data?.controlsLot && (
        <div className="grid gap-3 sm:col-span-12 sm:grid-cols-4">
          {line.direction === "entrada" && (
            <Field label="Lote">
              <Select
                value={line.lotMode}
                onChange={(e) => set("lotMode", e.target.value as Line["lotMode"])}
              >
                <option value="existente">Lote existente</option>
                <option value="novo">Lote novo</option>
              </Select>
            </Field>
          )}
          {line.lotMode === "existente" || line.direction === "saida" ? (
            <Field label="Qual lote">
              <LotSelect
                productId={line.productId}
                warehouseId={line.direction === "saida" ? warehouseId : undefined}
                onlyWithBalance={line.direction === "saida"}
                value={line.lotId}
                onChange={(v) => set("lotId", v)}
              />
            </Field>
          ) : (
            <>
              <Field label="Código do lote novo">
                <Input
                  value={line.newLotCode}
                  onChange={(e) => set("newLotCode", e.target.value)}
                />
              </Field>
              <Field label="Validade">
                <Input
                  type="date"
                  value={line.newLotExpires}
                  onChange={(e) => set("newLotExpires", e.target.value)}
                />
              </Field>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AdjustmentView({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const doc = useQuery({
    queryKey: ["estoque", "ajustes", "detalhe", id],
    queryFn: () => apiFetch<Adjustment>(`/estoque/ajustes/${id}`),
  });
  const reverse = useDocAction<{ reason: string }, Adjustment>({
    path: () => `/estoque/ajustes/${id}/estornar`,
    invalidate: [["estoque"]],
  });
  const a = doc.data;
  return (
    <Card
      title={a ? `Ajuste ${a.number}` : "Ajuste"}
      actions={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {!a ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-6">
            <span>
              <DocStatusBadge status={a.status} />
            </span>
            <span>Depósito: {a.warehouseName}</span>
            <span>Motivo: {a.reason}</span>
            <span>Por: {a.createdByEmail ?? "—"}</span>
            <span>Em: {dateTimeBR(a.confirmedAt)}</span>
          </div>
          {a.status === "estornado" && (
            <Alert tone="warn">
              Estornado em {dateTimeBR(a.reversedAt)}. Motivo: {a.reversalReason}
            </Alert>
          )}
          <Table head={["Produto", "Lote", "Movimento", "Quantidade"]}>
            {a.items?.map((i) => (
              <tr key={i.id}>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs">{i.productCode}</span> {i.productName}
                </td>
                <td className="px-3 py-2 font-mono">{i.lotCode ?? "—"}</td>
                <td className="px-3 py-2">
                  {i.direction > 0 ? (
                    <Badge tone="ok">entrada</Badge>
                  ) : (
                    <Badge tone="error">saída</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{qty(i.quantity, i.unitCode)}</td>
              </tr>
            ))}
          </Table>
          {a.status === "confirmado" && can("estoque.ajuste.lancar") && (
            <ReverseAction
              pending={reverse.isPending}
              error={reverse.error}
              onConfirm={(reason) => reverse.mutate({ reason })}
            />
          )}
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ depósitos

export function DepositosPage() {
  const canEdit = useCan()("admin.empresa.editar");
  const whs = useWarehouses();
  const [editing, setEditing] = useState<Warehouse | "new" | null>(null);
  return (
    <div className="space-y-6">
      <PageTitle
        title="Depósitos"
        description="Onde o estoque fica: a fábrica (padrão), o estoque de cada vendedor (criado com o vendedor) e outros locais."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Novo depósito
            </Button>
          )
        }
      />
      {editing && (
        <WarehouseEditor
          key={editing === "new" ? "new" : editing.id}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        {whs.isPending ? (
          <Spinner />
        ) : (
          <Table head={["Código", "Nome", "Tipo", "Situação", ""]}>
            {whs.data?.map((w) => (
              <tr key={w.id}>
                <td className="px-3 py-2 font-mono">{w.code}</td>
                <td className="px-3 py-2">
                  {w.name} {w.isDefault && <Badge tone="ok">padrão</Badge>}
                </td>
                <td className="px-3 py-2">
                  {WAREHOUSE_KIND_LABEL[w.kind]}
                  {w.salespersonName ? ` — ${w.salespersonName}` : ""}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={w.status} />
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(w)}>
                      Editar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

function WarehouseEditor({ item, onClose }: { item: Warehouse | null; onClose: () => void }) {
  const f = useForm({
    code: item?.code ?? "",
    name: item?.name ?? "",
    kind: item?.kind === "terceiro" ? "terceiro" : "fabrica",
    isDefault: item?.isDefault ?? false,
    status: item?.status ?? "active",
  });
  const auto = useAutoCode("deposito");
  const action = useDocAction<unknown, Warehouse>({
    path: () => (item ? `/estoque/depositos/${item.id}` : "/estoque/depositos"),
    method: item ? "PUT" : "POST",
    invalidate: [["estoque", "depositos"]],
    onDone: onClose,
    successMessage: (r) =>
      item
        ? "Depósito atualizado com sucesso."
        : `Depósito criado com sucesso. Código: ${r.code}${auto.note(r.code)}.`,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = item
      ? warehouseUpdateSchema.safeParse({ ...f.values, version: item.version })
      : warehouseInputSchema.safeParse({ ...f.values, code: auto.forSave(f.values.code) });
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    action.mutate(parsed.data);
  }

  return (
    <Card title={item ? `Editar ${item.name}` : "Novo depósito"}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4">
        <CodeField
          auto={auto}
          value={f.values.code}
          onChange={(v) => f.set("code")(v)}
          error={f.errors["code"]}
          disabled={Boolean(item)}
        />
        <Field label="Nome" error={f.errors["name"]}>
          <Input {...f.text("name")} />
        </Field>
        <Field label="Tipo">
          <Select {...f.text("kind")} disabled={Boolean(item)}>
            <option value="fabrica">Fábrica</option>
            <option value="terceiro">Terceiros / outro</option>
          </Select>
        </Field>
        <Field label="Situação" error={f.errors["status"]}>
          <Select {...f.text("status")}>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
          </Select>
        </Field>
        <label className="inline-flex items-center gap-2 text-sm sm:col-span-4">
          <input type="checkbox" className="size-4 accent-primary" {...f.check("isDefault")} />
          Depósito padrão da fábrica (produção e ajustes começam por ele)
        </label>
        <div className="flex gap-2 sm:col-span-4">
          <Button type="submit" disabled={action.isPending}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <div className="sm:col-span-4">
          <SaveError error={action.error} />
        </div>
      </form>
    </Card>
  );
}
